import React, { useState, useEffect } from "react";
import { TouchableOpacity } from "react-native";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import {
  handleBiometricLogin,
  checkBiometricAvailability,
} from "@scenes/authentication/utils/Biometrics/index";
import * as LocalAuthentication from "expo-local-authentication";
import styled from "styled-components/native";
import { Spacer, VDBText } from "@components/atoms";
import { getFromSecureStore } from "@utils/index";
import { useTranslation } from "react-i18next";
import { SpaceType } from "@components/atoms/Spacer/types";
import { VDBIcons } from "@utils/VDBIcons";

type BiometricProps = {
  onBiometricLoginSuccess: any;
};

const BiometricAuthenticationButton = ({
  onBiometricLoginSuccess,
}: BiometricProps) => {
  const [biometricType, setBiometricType] = useState<string | null>(null);
  const [enrolledLevel, setEnrolledLevel] =
    useState<LocalAuthentication.SecurityLevel | null>(null);
  const [showBiometrics, setShowBiometrics] = useState(false);
  const { t } = useTranslation();

  const BiometricAvailability = async () => {
    const isBiometricAvailable = await checkBiometricAvailability();

    if (isBiometricAvailable) {
      const supportedBiometrics =
        await LocalAuthentication.supportedAuthenticationTypesAsync();
      const enrolledType = await LocalAuthentication.getEnrolledLevelAsync();

      if (
        enrolledType === LocalAuthentication.SecurityLevel.BIOMETRIC_STRONG ||
        enrolledType === LocalAuthentication.SecurityLevel.BIOMETRIC_WEAK
      ) {
        if (
          supportedBiometrics.includes(
            LocalAuthentication.AuthenticationType.FACIAL_RECOGNITION
          ) &&
          supportedBiometrics.includes(
            LocalAuthentication.AuthenticationType.FINGERPRINT
          )
        ) {
          setBiometricType("both");
        } else {
          if (
            supportedBiometrics.includes(
              LocalAuthentication.AuthenticationType.FACIAL_RECOGNITION
            )
          ) {
            setBiometricType("face");
          } else if (
            supportedBiometrics.includes(
              LocalAuthentication.AuthenticationType.FINGERPRINT
            )
          ) {
            setBiometricType("fingerprint");
          } else {
            setBiometricType(null);
          }
        }
      } else {
        setBiometricType(null);
      }
      setEnrolledLevel(enrolledType);
    } else {
      setBiometricType(null);
    }
  };
  useEffect(() => {
    const checkCredentials = async () => {
      const username = await getFromSecureStore("username");
      const password = await getFromSecureStore("password");
      const biometricEnabled = await getFromSecureStore("biometricEnabled");
      if (
        username &&
        password &&
        enrolledLevel === LocalAuthentication.SecurityLevel.BIOMETRIC_STRONG &&
        biometricEnabled === "true"
      ) {
        setShowBiometrics(true);
      } else {
        setShowBiometrics(false);
      }
    };
    BiometricAvailability().then();
    checkCredentials();
  }, [enrolledLevel]);

  const onPressBiometric = async () => {
    const success = await handleBiometricLogin();

    if (success) {
      onBiometricLoginSuccess();
    }
  };

  return biometricType && enrolledLevel && showBiometrics ? (
    <>
      <Spacer size={SpaceType.Big} />
      <BiometricsViewContainer>
        <LineView />
        <VDBText>
          {biometricType === "both"
            ? ""
            : t("login.screenText.signInWithFaceID.text")}
          {biometricType === "face" && "Face ID"}
          {biometricType === "fingerprint" && "Touch ID"}
          {biometricType === "both" && "Biometric Authentication"}
        </VDBText>
        <LineView />
      </BiometricsViewContainer>
      <Spacer size={SpaceType.Big} />
      <TouchableOpacity onPress={onPressBiometric}>
        <VDBIcon
          size={48}
          variant={IconsVariant.VDBCustom}
          name={biometricType === "face" ? VDBIcons.FACE_ID : VDBIcons.TOUCH_ID}
        />
      </TouchableOpacity>
    </>
  ) : null;
};

const BiometricsViewContainer = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 16px;
`;

const LineView = styled.View`
  height: 1px;
  background-color: ${({ theme }) => theme["core-04"]};
  width: 100%;
`;

export default BiometricAuthenticationButton;
